import { validate as isUUID } from 'uuid';
import { type Team } from '~/common/api-client';
import billingClient, {
  type EnterpriseMembers,
  memberIsPending
} from '~/common/utils/billing-client/client-v2';

export const getCurrentUserIds = (team: Team): string[] => {
  /* 
  NOTE: If `Set.difference` and `Set.union` become supported, this could become:
  const userIds = new Set(team.user_ids)
    .union(new Set(team.user_ids_to_add))
    .difference(new Set(team.user_ids_to_remove));
  */
  const userIds = new Set(team.user_ids);
  team.user_ids_to_add.forEach(id => userIds.add(id));
  team.user_ids_to_remove.forEach(id => userIds.delete(id));
  return Array.from(userIds);
};

export const deduplicateUserIds = (
  team: Team,
  userIdsToAdd: string[],
  userIdsToRemove: string[]
): [string[], string[]] => {
  // Set of all user IDs that are current members of the team
  const currentUserIds = new Set(team.user_ids);
  // Deduplicated set of IDs that should be added to the team
  const userIdsAdded = new Set([...team.user_ids_to_add, ...userIdsToAdd]);
  // Deduplicated set of IDs that should be removed from the team
  const userIdsRemoved = new Set([
    ...team.user_ids_to_remove,
    ...userIdsToRemove
  ]);
  // A user cannot be both added and removed
  userIdsAdded.forEach(id => {
    if (userIdsRemoved.has(id)) {
      userIdsAdded.delete(id);
      userIdsRemoved.delete(id);
    }
  });
  // Users should not be added to the team twice
  currentUserIds.forEach(id => userIdsAdded.delete(id));
  return [Array.from(userIdsAdded), Array.from(userIdsRemoved)];
};

type SplitIdsAndEmailsResult = {
  keycloakIds: string[];
  invitedEmails: string[];
};

export const splitUserIdsAndEmails = (
  userIdsOrEmails: string[]
): SplitIdsAndEmailsResult => {
  const result = userIdsOrEmails.reduce(
    (acc, idOrEmail) => {
      if (isUUID(idOrEmail)) {
        acc.keycloakIds.push(idOrEmail);
      } else {
        acc.invitedEmails.push(idOrEmail);
      }

      return acc;
    },
    { keycloakIds: [], invitedEmails: [] } as SplitIdsAndEmailsResult
  );

  return result;
};

export type UpdatePendingUserTeamAccessParams = {
  token: string;
  enterpriseId: string;
  invitedEmails: string[];
  teamId: string;
  enterpriseMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  actionType?: UpdatePendingUserTeamAccessAction;
};

export enum UpdatePendingUserTeamAccessAction {
  Add = 'add',
  Remove = 'remove'
}

const getInvitationIdsByEmails = (
  emails: string[],
  members: EnterpriseMembers.PendingMember[]
): string[] => {
  const invitationIds = emails.map(
    targetEmail =>
      members.find(({ email }) => email === targetEmail)?.invitation_id ?? ''
  );

  return invitationIds.filter(Boolean);
};

export const updatePendingUserTeamAccess = async ({
  token,
  enterpriseId,
  invitedEmails,
  teamId,
  enterpriseMembers,
  actionType = UpdatePendingUserTeamAccessAction.Add
}: UpdatePendingUserTeamAccessParams) => {
  const pendingMembers = enterpriseMembers.filter(member =>
    memberIsPending(member)
  ) as EnterpriseMembers.PendingMember[];

  const invitationIds = getInvitationIdsByEmails(invitedEmails, pendingMembers);

  const invitations = await Promise.all(
    invitationIds.map(invitationId =>
      billingClient.enterpriseInvitations.get({
        token,
        enterpriseId,
        invitationId
      })
    )
  );

  await Promise.all(
    invitations.map(({ id: invitationId, team_ids }) => {
      const teamIds =
        actionType === UpdatePendingUserTeamAccessAction.Add
          ? [...(team_ids || []), teamId]
          : team_ids?.filter(id => id !== teamId) || [];

      return billingClient.enterpriseInvitations.update({
        token,
        enterpriseId,
        invitationId,
        teamIds
      });
    })
  );

  return invitations;
};
